import * as React from 'react';
import { Clock } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';

export interface TimerWidgetProps {
  isCheckedIn: boolean;
  checkinTime?: string; // ISO timestamp
  onCheckin: () => void;
  onCheckout: () => void;
  className?: string;
}

// Format seconds to HH:MM:SS
function formatTime(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  return [hours, minutes, secs]
    .map((val) => String(val).padStart(2, '0'))
    .join(':');
}

// Calculate elapsed seconds from checkin time
function getElapsedSeconds(checkinTime: string): number {
  const checkinDate = new Date(checkinTime);
  const now = new Date();
  const diffMs = now.getTime() - checkinDate.getTime();
  return Math.max(0, Math.floor(diffMs / 1000));
}

export const TimerWidget = React.forwardRef<HTMLDivElement, TimerWidgetProps>(
  ({ isCheckedIn, checkinTime, onCheckin, onCheckout, className }, ref) => {
    const [elapsedSeconds, setElapsedSeconds] = React.useState(0);

    // Timer effect
    React.useEffect(() => {
      if (!isCheckedIn || !checkinTime) {
        setElapsedSeconds(0);
        return;
      }

      // Set initial elapsed time
      setElapsedSeconds(getElapsedSeconds(checkinTime));

      // Update timer every second
      const interval = setInterval(() => {
        setElapsedSeconds(getElapsedSeconds(checkinTime));
      }, 1000);

      return () => clearInterval(interval);
    }, [isCheckedIn, checkinTime]);

    const displayTime = formatTime(elapsedSeconds);

    return (
      <Card
        ref={ref}
        className={cn(
          'flex flex-col items-center justify-center gap-6 p-8 bg-gradient-to-br',
          isCheckedIn
            ? 'from-[var(--color-success)]/5 to-[var(--color-success)]/10'
            : 'from-[var(--color-muted)]/5 to-[var(--color-muted)]/10',
          className,
        )}
      >
        {/* Clock icon */}
        <div
          className={cn(
            'rounded-full p-4',
            isCheckedIn
              ? 'bg-[var(--color-success)]/10'
              : 'bg-[var(--color-muted)]/10',
          )}
        >
          <Clock
            className={cn(
              'h-8 w-8',
              isCheckedIn
                ? 'text-[var(--color-success)]'
                : 'text-[var(--color-muted-foreground)]',
            )}
          />
        </div>

        {/* Timer display */}
        <div className="flex flex-col items-center gap-2">
          <p className="text-sm font-medium text-[var(--color-muted-foreground)]">
            {isCheckedIn ? 'Time Worked' : 'Not Checked In'}
          </p>
          <p className="font-mono text-5xl font-bold text-[var(--color-foreground)]">
            {displayTime}
          </p>
        </div>

        {/* Status text */}
        {isCheckedIn && checkinTime && (
          <p className="text-xs text-[var(--color-muted-foreground)]">
            Checked in at {new Date(checkinTime).toLocaleTimeString()}
          </p>
        )}

        {/* Action buttons */}
        <div className="flex gap-3 w-full pt-4">
          {!isCheckedIn ? (
            <Button
              onClick={onCheckin}
              className="flex-1 bg-[var(--color-success)] hover:bg-[var(--color-success)]/90 text-white"
            >
              Check In
            </Button>
          ) : (
            <Button
              onClick={onCheckout}
              className="flex-1 bg-[var(--color-destructive)] hover:bg-[var(--color-destructive)]/90 text-white"
            >
              Check Out
            </Button>
          )}
        </div>
      </Card>
    );
  },
);

TimerWidget.displayName = 'TimerWidget';
